import { useEffect, useState, type RefObject } from 'react';
import { Icon, IconButton } from '../../ui';
import type { IconName } from '../../ui';
import type { GuestRequestView } from '../../api/guest';
import { computeDisplayEtaSec, describeQueuePosition, reconcileDisplayEtaOnRefetch } from './guestRefetch';
import { describeRequestAge } from './guestRequestAge.ts';
import { useT } from '../../i18n/i18n.tsx';

type Tone = 'live' | 'ok' | 'danger' | 'info';

const TONE_BY_STATUS: Record<GuestRequestView['status'], Tone> = {
  pending: 'live',
  accepted: 'live',
  played: 'ok',
  rejected: 'danger',
  expired: 'info',
};

// MVP-11 #29: the icon per tone, none of them a `Spinner` — `pending`/`accepted` are a live status,
// not a network fetch in progress, so a spinning icon here was decorative rather than meaningful
// (DESIGN.md §6.2's loading state is for an actual in-flight request). `clock` reads as "waiting" /
// "in progress" without implying anything is loading.
const ICON_BY_TONE: Record<Tone, IconName> = {
  live: 'clock',
  ok: 'check',
  danger: 'alert',
  info: 'info',
};

// MVP-11 #32: only a request that is truly over for tonight gets a dismiss control — a live
// pending/accepted card must keep reappearing (it is not stale, it is the guest's actual status).
// MVP-11 #3: exported so `RequestPage` can gate `isRequestDismissed` on the same terminal set —
// a request can go terminal -> live again (KJ undoes a removal), and a dismissed id must stop
// hiding the card once that happens.
export const DISMISSIBLE_STATUSES: ReadonlySet<GuestRequestView['status']> = new Set(['rejected', 'expired', 'played']);

/**
 * DESIGN.md §9.3 [Requests] `QueuePositionCard` — DESIGN.md §10's Guest-layout "bottom bar
 * showing 'You're #N · ~M min'" (AC-REQ-8's explicit, live-updating position/ETA state). Purely
 * presentational: `RequestPage` is the one subscribing to `request.mine` SSE frames and
 * re-fetching `myRequests()` on `visibilitychange`; this only ever renders whatever
 * `GuestRequestView` it was handed, exactly like every other domain component in this feature.
 */
export function QueuePositionCard({
  request,
  fetchedAt,
  connectionHint = null,
  onDismiss,
  containerRef,
}: {
  request: GuestRequestView;
  /** R10-P2-3(a) fix: `Date.now()` at the moment `RequestPage` received THIS `request` object
   * (a fresh `GET /api/requests/mine` response, a submit/duplicate result, or a merged
   * `request.mine` frame) — paired with `request.etaSec`'s server-computed snapshot so the 1s
   * tick below can derive a genuinely counting-down display instead of a frozen number. Omitted
   * entirely (not merely `undefined` at runtime) only by tests exercising `describe()` directly;
   * every real caller always has one alongside `request`. */
  fetchedAt?: number;
  /** R9-P1-D fix (round 9 critic): set while `RequestPage`'s own `RealtimeClient` is degraded,
   * offline, or reauth-ing — a card that looks frozen during a dead SSE stream must read as
   * "still working, just slower" (the 15s poll fallback IS still running underneath it), never as
   * silent failure or an error state. MVP-11 #16: the text itself is `guestRefetch.ts`'s
   * `guestConnectionHint(connectionState)` — the ONE connection language DESIGN.md §6.4
   * specifies (Package E's `connectionLabel`), not a phrasing this card invents on its own. */
  connectionHint?: string | null;
  /** MVP-11 #32: only ever supplied for a dismissible (terminal) status — `RequestPage` owns
   * persisting the dismissal (`dismissedRequest.ts`) and no longer rendering this request. */
  onDismiss?: () => void;
  /** MVP-491: `RequestPage` moves focus in here after a submit lands — see `guestResultsNav.ts`'s
   * `resolveQueueCardFocus`. Handed down as a ref rather than as an `autoFocus` flag because only
   * the page knows WHY this card appeared, and most of the reasons (the load-time `myRequests()`
   * fetch, a `request.mine` frame, the 15s poll) must not touch focus at all. */
  containerRef?: RefObject<HTMLDivElement | null>;
}) {
  const t = useT();
  const tone = TONE_BY_STATUS[request.status];

  // R10-P2-3(a) fix: re-render once a second while this card shows a live ETA, so
  // `computeDisplayEtaSec` below actually counts down instead of only updating on the next
  // network refetch/push.
  // MVP-11 #32: a terminal card has no ETA to tick, but still shows "asked m:ss ago" — ticking it
  // much more slowly (a minute, not a second) keeps that age honest for as long as the card stays
  // on screen without wasting renders on a number nobody is watching to the second.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const live = request.status === 'pending' || request.status === 'accepted';
    const id = setInterval(() => setNow(Date.now()), live ? 1000 : 60_000);
    return () => clearInterval(id);
  }, [request.status]);

  // R11-P2-3 fix (round 11 critic, reproduced live): a fresh `request.etaSec`/`fetchedAt` pair used
  // to be trusted outright on every refetch, even when `request.position` had not changed — the
  // server's etaSec is only ever a coarse estimate recomputed from scratch each call, so a guest
  // ticking the card down 1s/s locally (`computeDisplayEtaSec` below) would see the number jump
  // BACK UP the moment a refetch landed with a slightly larger fresh value. `etaBaseline` is the
  // reconciled (never-increasing-for-the-same-position) `{etaSec, fetchedAt}` pair `now` actually
  // counts down from; `reconcileDisplayEtaOnRefetch` (`guestRefetch.ts`) is the pure decision of
  // what that baseline should become whenever a new `request` snapshot arrives.
  const [etaBaseline, setEtaBaseline] = useState<{ position: number | undefined; etaSec: number; fetchedAt: number } | null>(() =>
    typeof request.etaSec === 'number' ? { position: request.position, etaSec: request.etaSec, fetchedAt: fetchedAt ?? Date.now() } : null
  );
  useEffect(() => {
    if (typeof request.etaSec !== 'number') {
      setEtaBaseline(null);
      return;
    }
    const nextFetchedAt = fetchedAt ?? Date.now();
    setEtaBaseline((prev) => {
      const prevDisplayEtaSec = prev ? computeDisplayEtaSec(prev.etaSec, prev.fetchedAt, Date.now()) : undefined;
      const etaSec = reconcileDisplayEtaOnRefetch({
        prevPosition: prev?.position,
        prevDisplayEtaSec,
        nextPosition: request.position,
        nextEtaSec: request.etaSec,
      });
      return typeof etaSec === 'number' ? { position: request.position, etaSec, fetchedAt: nextFetchedAt } : null;
    });
    // Deliberately keyed on the raw inputs, not `prev`/derived values — this must re-run exactly
    // once per genuinely new `request` snapshot (a new fetchedAt), not on every 1s tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.etaSec, request.position, fetchedAt]);

  const displayEtaSec = etaBaseline ? computeDisplayEtaSec(etaBaseline.etaSec, etaBaseline.fetchedAt, now) : request.etaSec;

  // MVP-11 #32: age + dismiss are only for a request that is over for tonight — a live
  // pending/accepted card is not stale, it is simply still waiting, so it shows neither.
  const isTerminal = DISMISSIBLE_STATUSES.has(request.status);
  const createdAtMs = new Date(request.createdAt).getTime();
  // MVP-11 #9: `formatDuration`'s m:ss/h:mm:ss reads absurd for an old card ("Asked 168:00:00
  // ago") — human-bucket phrasing instead.
  // MVP-272 Phase 1: `guestRequestAge.ts`'s `describeRequestAge` is the guest-translated version of
  // `lib/format.ts`'s `formatRelativeAge` (untouched this phase — see that module's own header
  // comment for why the two deliberately duplicate their thresholds instead of sharing them).
  const ageDescription = describeRequestAge(Number.isFinite(createdAtMs) ? Math.max(0, now - createdAtMs) : null);
  const ageLabel = isTerminal && Number.isFinite(createdAtMs) ? t('guest.queue.askedPrefix', { age: t(ageDescription.key, ageDescription.params) }) : null;
  const dismissible = isTerminal && Boolean(onDismiss);
  const positionDescription = describeQueuePosition(request, displayEtaSec);
  const detailLabel = t(positionDescription.key, positionDescription.params);

  return (
    // MVP-491: `tabIndex={-1}` so `.focus()` works here, and ONLY that — a positive/zero tabindex
    // would put a non-interactive status card into every guest's Tab path forever, between the
    // results and the sheet. It is reachable when the page sends the guest here and invisible to
    // Tab the rest of the time. `role="status"` stays for the sighted-free case it already served;
    // the focus move is what makes a reader actually read it, since a live region that is INSERTED
    // whole (this card only renders once there is a request) is not reliably announced.
    <div
      className={`queue-position-card queue-position-card--${tone}`}
      role="status"
      data-testid="queue-position-card"
      ref={containerRef}
      tabIndex={-1}
    >
      <span className={`queue-position-card__icon queue-position-card__icon--${tone}`} aria-hidden="true">
        <Icon name={ICON_BY_TONE[tone]} size={20} />
      </span>
      <span className="queue-position-card__body">
        <span className="queue-position-card__title text-body truncate">{request.songTitle ?? t('guest.queue.songFallback')}</span>
        <span className="queue-position-card__detail text-meta">{detailLabel}</span>
        {connectionHint && (
          <span className="queue-position-card__hint text-meta" data-testid="live-updates-paused-hint">
            {connectionHint}
          </span>
        )}
        {ageLabel && (
          <span className="queue-position-card__age text-meta" data-testid="queue-position-card-age">
            {ageLabel}
          </span>
        )}
      </span>
      {dismissible && (
        <IconButton
          icon="close"
          label={t('guest.queue.dismiss')}
          size="sm"
          className="queue-position-card__dismiss"
          onClick={onDismiss}
        />
      )}
    </div>
  );
}
